import { Button, Center, chakra, Flex, Grid, GridItem, Image, Text, Box } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";
import { getRandomSeed } from "@noli/core";
import { langStore } from "@noli/business";

import useImageWithPlaceholder from "@/modules/editor/hooks/useImageWithPlaceholder";
import { useSelectedImgSize } from "@/modules/editor/hooks/useSelectedImgSize";

import { Actions } from "@/utils/api";
import { AI_MAX_WH, SD_VERSION } from "@/utils/constants";
import {
  ACG_PROMPT_NEGATIVE,
  ACG_STYLES_LIST,
  ACG_STYLE_TYPES,
  MODEL_TYPES_INFO,
} from "@/modules/editor/constant";
import { Editor_Words } from "@/utils/lang/editor";

import { useOwnToast } from "@/utils/misc";
import { postImg2Prompt } from "@/modules/editor/request/prompt";

import { ImageWithPlaceHolder } from "@/components/Image/WithPlaceHolder";
import { InfoText, LabelText } from "@/modules/editor/components/styleComponents/Texts";
import { useTranslate } from "@/utils/lang";
import { SafeOp } from "@/utils/safeOp";
import { GenerateBizType } from "@/types/creator";
import BizGenerateButton from "@/components/bussiness/BizGenerateButton";
import { GeneratePriceRules } from "@/modules/editor/hooks/useGenerateCost";

const IACGPanel: React.FC = () => {
  const { url, placeholder, alias, enable } = useImageWithPlaceholder();
  const { imgWH, displayNode } = useSelectedImgSize();
  const { translate } = useTranslate();
  const lang = langStore.tgt;
  const toastFn = useOwnToast();
  const [styleType, setStyleType] = useState<ACG_STYLE_TYPES>("Chinese_style");

  const suitableWH = useMemo(() => {
    if (!displayNode || !imgWH) return { w: 512, h: 512 };

    const wh = { w: imgWH.w, h: imgWH.h };
    if (wh.w * wh.h > AI_MAX_WH ** 2) {
      const { w, h } = displayNode.wh;
      if (w * h < wh.w * wh.h) {
        wh.w = w;
        wh.h = Math.abs(h);
      }
    }

    wh.w = Number(wh.w.toFixed(0));
    wh.h = Number(wh.h.toFixed(0));
    return wh;
  }, [imgWH, displayNode]);

  const sizeString = useMemo(() => {
    return suitableWH
      ? `${suitableWH.w} × ${suitableWH.h} px`
      : translate(Editor_Words["general-loading"]);
  }, [suitableWH]);

  async function handleGenerate() {
    if (!alias || !url || displayNode?.type !== "image" || !imgWH) {
      return;
    }

    const busyAliases = [alias];
    const virtualNodeInfo = await SafeOp.setupPostCreator(
      toastFn,
      "img2img",
      lang,
      busyAliases,
      imgWH,
    );

    const config = ACG_STYLES_LIST.find((item) => item.style === styleType);
    const fidelity = 0.3; // config?.fidelity ??
    const negativePrompt = config?.negativePrompt ?? ACG_PROMPT_NEGATIVE;

    postImg2Prompt({
      image_url: url,
      style: styleType,
    })
      .then((prompt) => {
        return Actions.importMeta({
          t: toastFn,
          lang,
          type: "img2img",
          meta: {
            type: "img2img",
            data: {
              w: imgWH.w,
              h: imgWH.h,
              url: "",
              text: prompt,
              fidelity,
              use_circular: false,
              is_anime: true,
              seed: getRandomSeed(),
              model: "anime",
              promptInfo: {
                lang,
                original: prompt,
                originalNegative: negativePrompt,
              },
            },
          },
          setupInfo: { checkBusy: false, busyAliases, virtualNodeInfo },
          aliasPrefix: "img2img",
          opt: { freezePrompt: false, displayNode },
          bizType: GenerateBizType.Presets,
        });
      })
      .catch((err) => {
        console.error("postImg2Prompt err: ", err);
        SafeOp.cleanupSetup(busyAliases, virtualNodeInfo);
      });
  }

  return (
    <Flex flexDir="column" mt="16px" pb="24px" pos="relative">
      <LabelText>{translate(Editor_Words["label_select_an_image"])}</LabelText>
      <ImageWithPlaceHolder url={url} placeholderText={placeholder} uploadable />
      <LabelText mt="8px">{translate(Editor_Words["label_select_style"])}</LabelText>
      <Grid gridTemplateColumns="repeat(4, 1fr)" gap="8px" mt="8px">
        {ACG_STYLES_LIST.map((item) => (
          <GridItem key={item.style}>
            <Flex flexDir="column" alignItems="center">
              <Box
                w="100%"
                h="auto"
                mb="4px"
                pos="relative"
                cursor="pointer"
                rounded="4px"
                overflow="hidden"
                _after={{
                  content: `""`,
                  pos: "absolute",
                  top: 0,
                  left: 0,
                  w: "100%",
                  h: "100%",
                  rounded: "4px",
                  border: styleType === item.style ? "2px solid #36C19E" : "none",
                }}
                onClick={() => setStyleType(item.style)}
              >
                <Image w="100%" h="auto" src={item.previewUrl} />
              </Box>
              <Text color="text.caption" fontSize="12px" textAlign="center">
                {lang === "zh" ? item.label : item.style.split("_")[0]}
              </Text>
            </Flex>
          </GridItem>
        ))}
      </Grid>
      {enable ? (
        <Flex flexDirection="column" gap="12px" mt="16px">
          <InfoText>
            {translate(Editor_Words["model"])}：{translate(MODEL_TYPES_INFO["anime"]?.showInfo)}
          </InfoText>
          <InfoText>
            {translate(Editor_Words["resolution"])}： {sizeString}
          </InfoText>
        </Flex>
      ) : null}
      <BizGenerateButton
        mt="48px"
        sticky
        buttonProps={{
          isDisabled: !enable,
        }}
        onSubmit={handleGenerate}
        size={imgWH}
        checkBusyConfig={{
          checkAll: true,
          busyAlias: alias,
        }}
        calcPriceProps={GeneratePriceRules[GenerateBizType.Presets]}
      >
        {translate(Editor_Words["acg_generate_btn_text"])}
      </BizGenerateButton>
    </Flex>
  );
};

export const ACGPanel = chakra(observer(IACGPanel));
